import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Brain, Sparkles, BookOpen, Timer, Repeat, Target, Layers, Lightbulb, HeartPulse, ChevronRight, Wand2, RotateCcw, Accessibility } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/config";
import { StudyPlanCard } from "@/components/StudyPlanCard";
import { ClassNotesSection } from "@/components/ClassNotesSection";
import { StudyBreaksSection } from "@/components/StudyBreaksSection";
import { MathText } from "@/components/MathText";

export const Route = createFileRoute("/_app/learn")({
  head: () => ({
    meta: [
      { title: i18n.t("learn.meta.title") },
      { name: "description", content: i18n.t("learn.meta.description") },
    ],
  }),
  component: LearnPage,
});

const CHAPTER_IDS = ["memory", "brain", "mistakes", "notes", "anxiety", "forgetting"] as const;
type ChapterId = typeof CHAPTER_IDS[number];

const CHAPTER_ICONS: Record<ChapterId, typeof Brain> = {
  memory: Brain,
  brain: Sparkles,
  mistakes: Target,
  notes: BookOpen,
  anxiety: HeartPulse,
  forgetting: Lightbulb,
};

type Method = {
  id: MethodId;
  icon: typeof Sparkles;
  name: string;
  lvl: "easy" | "media";
};

type AdaptKey = "dsa" | "adhd" | "bes" | "focus";

const ADAPT_KEYS: AdaptKey[] = ["dsa", "adhd", "bes", "focus"];

const ADAPT_COLOR: Record<AdaptKey, string> = {
  dsa: "from-sky-500/15 to-sky-500/5 border-sky-500/30",
  adhd: "from-amber-500/15 to-amber-500/5 border-amber-500/30",
  bes: "from-violet-500/15 to-violet-500/5 border-violet-500/30",
  focus: "from-emerald-500/15 to-emerald-500/5 border-emerald-500/30",
};

const METHOD_IDS = [
  "feynman",
  "activeRecall",
  "pomodoro",
  "spacedRepetition",
  "cornellNotes",
  "blurting",
  "mindMapping",
] as const;
type MethodId = typeof METHOD_IDS[number];

const METHOD_NAMES: Record<MethodId, string> = {
  feynman: "Feynman",
  activeRecall: "Active Recall",
  pomodoro: "Pomodoro",
  spacedRepetition: "Spaced Repetition",
  cornellNotes: "Cornell Notes",
  blurting: "Blurting",
  mindMapping: "Mind Mapping",
};

const METHOD_ICONS: Record<MethodId, typeof Sparkles> = {
  feynman: Sparkles,
  activeRecall: Repeat,
  pomodoro: Timer,
  spacedRepetition: Layers,
  cornellNotes: BookOpen,
  blurting: Target,
  mindMapping: Brain,
};

const METHOD_LEVELS: Record<MethodId, "easy" | "media"> = {
  feynman: "easy",
  activeRecall: "easy",
  pomodoro: "easy",
  spacedRepetition: "media",
  cornellNotes: "easy",
  blurting: "media",
  mindMapping: "media",
};

const METHOD_HAS_ADAPTATIONS: Record<MethodId, boolean> = {
  feynman: true,
  activeRecall: true,
  pomodoro: true,
  spacedRepetition: true,
  cornellNotes: true,
  blurting: true,
  mindMapping: true,
};

const methods: Method[] = METHOD_IDS.map((id) => ({
  id,
  icon: METHOD_ICONS[id],
  name: METHOD_NAMES[id],
  lvl: METHOD_LEVELS[id],
}));

type MethodName = typeof methods[number]["name"];

function LearnPage() {
  const { t } = useTranslation();

  const chapters = CHAPTER_IDS.map((id) => ({
    id,
    icon: CHAPTER_ICONS[id],
    title: t(`learn.chapters.${id}.title`),
    body: t(`learn.chapters.${id}.body`),
  }));

  return (
    <div className="space-y-6 px-5 pt-8">
      <PageHeader
        tone="sky"
        icon={<Brain className="h-5 w-5" />}
        kicker={t("learn.header.badge")}
        title={t("learn.header.title")}
        subtitle={<>{t("learn.header.subtitlePre")}<b>{t("learn.header.subtitleBold")}</b>{t("learn.header.subtitlePost")}</>}
      />

      <section>
        <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{t("learn.science.heading")}</h2>
        <Card className="card-soft border-0 p-2">
          <Accordion type="single" collapsible className="w-full">
            {chapters.map((c, i) => (
              <AccordionItem key={i} value={`c-${i}`} className="border-b border-border/60 last:border-0">
                <AccordionTrigger className="px-3 text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-1.5 text-primary"><c.icon className="h-4 w-4" /></div>
                    <span className="text-sm font-medium">{c.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-4 text-sm leading-relaxed text-foreground/80">
                  {c.body.split("\n\n").map((p, k) => (
                    <p key={k} className="mb-2 whitespace-pre-line last:mb-0"><MathText>{p}</MathText></p>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{t("learn.methodsHeading")}</h2>
        <MethodsList />
      </section>

      <ClassNotesSection />

      <StudyBreaksSection />

      <Card className="card-soft border-0 p-5">
        <p className="text-sm font-semibold">{t("learn.finalTip.title")}</p>
        <p className="mt-1 text-sm text-foreground/80">
          {t("learn.finalTip.body")}
        </p>
      </Card>
    </div>
  );
}

function MethodsList() {
  const { t } = useTranslation();
  const [active, setActive] = useState<Method | null>(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [adaptTab, setAdaptTab] = useState<AdaptKey>("dsa");
  const qc = useQueryClient();

  const { data: saved } = useQuery({
    queryKey: ["study-method-quiz"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return null;
      const { data } = await supabase
        .from("profiles")
        .select("study_method_quiz, level")
        .eq("id", u.user.id)
        .maybeSingle();
      return {
        quiz: (data?.study_method_quiz ?? null) as SavedQuiz | null,
        level: (data?.level ?? 1) as number,
      };
    },
    staleTime: 60_000,
  });

  const savedQuiz = saved?.quiz ?? null;
  const userLevel = saved?.level ?? 1;

  const saveQuiz = useMutation({
    mutationFn: async (payload: SavedQuiz) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error(t("learn.toast.mustLogin"));
      const { error } = await supabase
        .from("profiles")
        .update({ study_method_quiz: payload })
        .eq("id", u.user.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["study-method-quiz"] });
      toast.success(t("learn.toast.saved"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const activeAdaptations = active ? getAdaptations(t, active.id) : undefined;
  const activeHow = active ? (t(`learn.methods.${active.id}.how`, { returnObjects: true }) as string[]) : [];
  const activeExample = active ? (t(`learn.methods.${active.id}.example`) as string) : "";
  const activeTip = active ? (t(`learn.methods.${active.id}.tip`, { defaultValue: "" }) as string) : "";

  return (
    <>
      <QuizCTA
        onOpen={() => setQuizOpen(true)}
        saved={savedQuiz}
        onPickSaved={(name) => {
          const m = methods.find((x) => x.name === name);
          if (m) setActive(m);
        }}
      />
      {savedQuiz && savedQuiz.top.length > 0 && (
        <StudyPlanCard
          methods={savedQuiz.top.slice(0, 3).map((t) => t.name)}
          level={userLevel}
        />
      )}
      <div className="space-y-2">
        {methods.map((m) => (
          <button
            key={m.name}
            onClick={() => setActive(m)}
            className="w-full text-left"
          >
            <Card className="card-soft border-0 p-4 transition hover:bg-muted/50 active:scale-[0.99]">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-primary/10 p-2 text-primary"><m.icon className="h-5 w-5" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">{m.name}</p>
                    <Badge variant="secondary" className="text-xs">{t(`learn.lvl.${m.lvl}`)}</Badge>
                      {METHOD_HAS_ADAPTATIONS[m.id] && (
                        <Badge variant="outline" className="gap-1 text-xs">
                          <Accessibility className="h-3 w-3" /> {t("learn.badge.accessible")}
                        </Badge>
                      )}
                  </div>
                  <p className="mt-0.5 text-sm text-foreground/80">{t(`learn.methods.${m.id}.d`)}</p>
                  <p className="mt-1 text-xs text-muted-foreground"><b>{t("learn.methodsList.when")}</b> {t(`learn.methods.${m.id}.when`)}</p>
                </div>
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              </div>
            </Card>
          </button>
        ))}
      </div>

      <MethodQuiz
        open={quizOpen}
        onOpenChange={setQuizOpen}
        initialSaved={savedQuiz}
        onComplete={(payload) => saveQuiz.mutate(payload)}
        onSelectMethod={(name) => {
          const m = methods.find((x) => x.name === name);
          if (m) {
            setQuizOpen(false);
            setTimeout(() => setActive(m), 150);
          }
        }}
      />

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto rounded-3xl sm:max-w-md">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-primary/10 p-2 text-primary">
                    <active.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <DialogTitle className="text-left">{active.name}</DialogTitle>
                    <div className="mt-0.5 flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">{t(`learn.lvl.${active.lvl}`)}</Badge>
                      <span className="text-xs text-muted-foreground">{t(`learn.methods.${active.id}.when`)}</span>
                    </div>
                  </div>
                </div>
                <DialogDescription className="pt-2 text-left">{t(`learn.methods.${active.id}.d`)}</DialogDescription>
              </DialogHeader>

              <div className="mt-2 space-y-4">
                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-primary">{t("learn.dialog.howTo")}</p>
                  <ol className="space-y-1.5 text-sm text-foreground/85">
                    {activeHow.map((step, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                          {i + 1}
                        </span>
                        <span className="leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-primary">{t("learn.dialog.example")}</p>
                  <div className="rounded-2xl bg-muted/60 p-3 text-sm leading-relaxed text-foreground/85">
                    {activeExample.split("\n\n").map((p, i) => (
                      <p key={i} className="mb-2 whitespace-pre-line last:mb-0">{p}</p>
                    ))}
                  </div>
                </div>

                {activeTip && (
                  <div className="flex items-start gap-2 rounded-2xl border border-primary/20 bg-primary/5 p-3 text-sm">
                    <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-foreground/85">{activeTip}</span>
                  </div>
                )}

                {activeAdaptations && (
                  <div>
                    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                      <Accessibility className="h-3.5 w-3.5" /> {t("learn.dialog.adaptationsTitle")}
                    </p>
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {ADAPT_KEYS
                        .filter((k) => activeAdaptations[k]?.length)
                        .map((k) => (
                          <button
                            key={k}
                            onClick={() => setAdaptTab(k)}
                            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                              adaptTab === k
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-background text-foreground/70 hover:bg-muted"
                            }`}
                          >
                            {t(`learn.adaptMeta.${k}.label`)}
                          </button>
                        ))}
                    </div>
                    {activeAdaptations[adaptTab]?.length ? (
                      <div
                        className={`rounded-2xl border bg-gradient-to-br p-3 ${ADAPT_COLOR[adaptTab]}`}
                      >
                        <p className="mb-2 text-xs text-muted-foreground">
                          {t("learn.dialog.adaptationsFor")} <span className="font-semibold text-foreground/80">{t(`learn.adaptMeta.${adaptTab}.short`)}</span>
                        </p>
                        <ul className="space-y-1.5 text-sm text-foreground/85">
                          {activeAdaptations[adaptTab]!.map((txt, i) => (
                            <li key={i} className="flex gap-2">
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                              <span className="leading-relaxed">{renderBoldMark(txt)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {t("learn.dialog.noAdaptations")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function getAdaptations(t: ReturnType<typeof useTranslation>["t"], id: MethodId): Partial<Record<AdaptKey, string[]>> | undefined {
  if (!METHOD_HAS_ADAPTATIONS[id]) return undefined;
  const out: Partial<Record<AdaptKey, string[]>> = {};
  for (const k of ADAPT_KEYS) {
    const list = t(`learn.methods.${id}.adaptations.${k}`, { returnObjects: true, defaultValue: [] }) as string[];
    if (Array.isArray(list) && list.length) out[k] = list;
  }
  return out;
}

function renderBoldMark(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<strong key={key++} className="font-semibold text-foreground">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ---------- Quiz "Scopri il tuo metodo" ----------

type QOption = { label: string; scores: Partial<Record<MethodName, number>> };
type QItem = { q: string; options: QOption[] };

const QUIZ_SCORES: Partial<Record<MethodName, number>>[][] = [
  [
    { Feynman: 2, "Active Recall": 1 },
    { "Mind Mapping": 2 },
    { "Active Recall": 2, Blurting: 1 },
  ],
  [
    { Pomodoro: 3 },
    { Pomodoro: 2 },
    { Blurting: 1 },
  ],
  [
    { "Cornell Notes": 2 },
    { "Cornell Notes": 1, "Mind Mapping": 1 },
    { "Cornell Notes": 2, "Active Recall": 1 },
  ],
  [
    { "Spaced Repetition": 3 },
    { "Spaced Repetition": 2, "Active Recall": 1 },
    { "Active Recall": 1 },
  ],
  [
    { Blurting: 3, Pomodoro: 1 },
    { "Spaced Repetition": 2, "Active Recall": 1 },
    { Pomodoro: 2, Blurting: 1 },
  ],
  [
    { Blurting: 2, "Cornell Notes": 1 },
    { Feynman: 2 },
    { "Mind Mapping": 2 },
  ],
  [
    { "Spaced Repetition": 2 },
    { Feynman: 2 },
    { "Mind Mapping": 2 },
  ],
];

type SavedQuiz = {
  top: { name: MethodName; score: number }[];
  answers: number[];
  taken_at: string;
};

function formatTakenAt(iso: string, lang: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function QuizCTA({
  onOpen,
  saved,
  onPickSaved,
}: {
  onOpen: () => void;
  saved: SavedQuiz | null;
  onPickSaved: (name: MethodName) => void;
}) {
  const { t, i18n } = useTranslation();
  if (saved && saved.top.length > 0) {
    const best = saved.top[0];
    return (
      <Card className="card-soft mb-3 border-0 bg-gradient-to-br from-[color-mix(in_oklab,var(--sky)_45%,var(--card))] to-[color-mix(in_oklab,var(--lilac)_35%,var(--card))] p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-white/70 p-2 dark:bg-white/10"><Wand2 className="h-5 w-5 text-primary" /></div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold">{t("learn.quiz.cta.yourMethod")}</p>
              <Badge className="text-xs">{t("learn.quiz.cta.recommendedBadge")}</Badge>
              <span className="text-xs text-muted-foreground">
                {t("learn.quiz.cta.savedOn", { date: formatTakenAt(saved.taken_at, i18n.language) })}
              </span>
            </div>
            <p className="mt-0.5 text-sm text-foreground/90">
              <span className="font-semibold">{best.name}</span>
              {saved.top[1] && (
                <span className="text-foreground/70">{t("learn.quiz.cta.thenPrefix")}{saved.top[1].name}
                  {saved.top[2] ? `${t("learn.quiz.cta.andWord")}${saved.top[2].name}` : ""}
                </span>
              )}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" className="h-8 rounded-xl" onClick={() => onPickSaved(best.name)}>
                {t("learn.quiz.cta.viewCard")}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 rounded-xl" onClick={onOpen}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> {t("learn.quiz.cta.retakeTest")}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card
      className="card-soft mb-3 border-0 bg-gradient-to-br from-[color-mix(in_oklab,var(--sky)_45%,var(--card))] to-[color-mix(in_oklab,var(--lilac)_35%,var(--card))] p-4 cursor-pointer transition active:scale-[0.99]"
      onClick={onOpen}
    >
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-white/70 p-2 dark:bg-white/10"><Wand2 className="h-5 w-5 text-primary" /></div>
        <div className="flex-1">
          <p className="text-sm font-semibold">{t("learn.quiz.cta.discoverTitle")}</p>
          <p className="mt-0.5 text-xs text-foreground/80">{t("learn.quiz.cta.discoverSubtitle")}</p>
        </div>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </Card>
  );
}

function MethodQuiz({
  open,
  onOpenChange,
  onSelectMethod,
  onComplete,
  initialSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSelectMethod: (name: MethodName) => void;
  onComplete: (payload: SavedQuiz) => void;
  initialSaved: SavedQuiz | null;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [saved, setSaved] = useState(false);

  const rawQuestions = t("learn.quiz.questions", { returnObjects: true }) as { q: string; options: string[] }[];
  const QUIZ: QItem[] = useMemo(
    () =>
      rawQuestions.map((q, i) => ({
        q: q.q,
        options: q.options.map((label, j) => ({ label, scores: QUIZ_SCORES[i]?.[j] ?? {} })),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  function pick(optIdx: number) {
    const next = [...answers, optIdx];
    setAnswers(next);
    if (step < QUIZ.length - 1) {
      setStep(step + 1);
    } else {
      setStep(QUIZ.length); // results
    }
  }

  function reset() {
    setStep(0);
    setAnswers([]);
    setSaved(false);
  }

  function results(): { name: MethodName; score: number }[] {
    const totals = new Map<MethodName, number>();
    answers.forEach((optIdx, qIdx) => {
      const opt = QUIZ[qIdx].options[optIdx];
      for (const [k, v] of Object.entries(opt.scores)) {
        totals.set(k as MethodName, (totals.get(k as MethodName) ?? 0) + (v ?? 0));
      }
    });
    return [...totals.entries()]
      .map(([name, score]) => ({ name, score }))
      .sort((a, b) => b.score - a.score);
  }

  const showResults = step >= QUIZ.length;
  const current = QUIZ[step];
  const top = showResults ? results().slice(0, 3) : [];

  // Auto-save the first time we hit the results screen with fresh answers
  useEffect(() => {
    if (!showResults || saved || top.length === 0) return;
    onComplete({
      top,
      answers,
      taken_at: new Date().toISOString(),
    });
    setSaved(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults]);

  // Suppress unused warning for initialSaved (reserved for future prefill).
  void initialSaved;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-h-[85dvh] overflow-y-auto rounded-3xl sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2 text-primary"><Wand2 className="h-5 w-5" /></div>
            <div>
              <DialogTitle className="text-left">{t("learn.quiz.dialogTitle")}</DialogTitle>
              <DialogDescription className="text-left">
                {showResults ? t("learn.quiz.resultsSubtitle") : t("learn.quiz.questionOf", { n: step + 1, total: QUIZ.length })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {!showResults && current && (
          <div className="mt-2 space-y-3">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${((step) / QUIZ.length) * 100}%` }}
              />
            </div>
            <p className="text-sm font-medium text-foreground/90">{current.q}</p>
            <div className="space-y-2">
              {current.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => pick(i)}
                  className="w-full rounded-2xl border border-border/60 bg-card p-3 text-left text-sm transition hover:border-primary/40 hover:bg-primary/5 active:scale-[0.99]"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {showResults && (
          <div className="mt-2 space-y-3">
            {top.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("learn.quiz.noResults")}</p>
            ) : (
              top.map((r, i) => {
                const m = methods.find((x) => x.name === r.name);
                if (!m) return null;
                const Icon = m.icon;
                return (
                  <button
                    key={r.name}
                    onClick={() => onSelectMethod(r.name)}
                    className="w-full text-left"
                  >
                    <Card
                      className={`card-soft border-0 p-4 transition hover:bg-muted/50 active:scale-[0.99] ${
                        i === 0
                          ? "bg-gradient-to-br from-[color-mix(in_oklab,var(--peach)_45%,var(--card))] to-[color-mix(in_oklab,var(--lilac)_35%,var(--card))]"
                          : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl bg-white/70 p-2 text-primary dark:bg-white/10"><Icon className="h-5 w-5" /></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {i === 0 && <Badge className="text-xs">{t("learn.quiz.recommendedBadge")}</Badge>}
                            <p className="text-sm font-semibold">{m.name}</p>
                            <Badge variant="secondary" className="text-xs">{t(`learn.lvl.${m.lvl}`)}</Badge>
                          </div>
                          <p className="mt-0.5 text-sm text-foreground/80">{t(`learn.methods.${m.id}.d`)}</p>
                          <p className="mt-1 text-xs text-muted-foreground"><b>{t("learn.methodsList.when")}</b> {t(`learn.methods.${m.id}.when`)}</p>
                        </div>
                        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                      </div>
                    </Card>
                  </button>
                );
              })
            )}
            <Button variant="ghost" className="w-full" onClick={reset}>
              <RotateCcw className="mr-2 h-4 w-4" /> {t("learn.quiz.retake")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
